import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { ASSETS, HUBS } from "@/lib/brand";
import { useApp } from "@/context/AppContext";
import { Logo } from "@/components/Logo";
import { LangToggle } from "@/components/LangToggle";
import { CeoSection } from "@/components/CeoSection";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { routeForUser } from "@/lib/nav";

export default function Entry() {
  const { user, t } = useApp();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate(routeForUser(user), { replace: true });
  }, [user, navigate]);

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Hero */}
      <div className="relative">
        <div className="absolute inset-0">
          <img src={ASSETS.logoSkyline} alt="" className="w-full h-full object-cover opacity-30" style={{ filter: "blur(2px)" }} />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(10,9,8,0.8) 0%, rgba(10,9,8,0.9) 55%, rgba(10,9,8,1) 100%)" }} />
        </div>

        <header className="relative z-10 flex items-center justify-between px-6 pt-6 max-w-4xl mx-auto">
          <span className="eyebrow">{t("private_club")}</span>
          <LangToggle />
        </header>

        <div className="relative z-10 max-w-2xl mx-auto px-6 pt-16 pb-24 text-center fade-up">
          <Logo size="lg" />
          <div className="eyebrow mt-6 text-[color:var(--cu-muted)]">{t("tagline")}</div>

          <div className="flex items-center justify-center gap-3 mt-7 flex-wrap">
            {HUBS.map((h) => (
              <span key={h} className="font-mono-cu text-[0.68rem] tracking-[0.22em] uppercase text-[color:var(--cu-muted)]">{h}</span>
            ))}
          </div>

          <p className="font-serif-cu italic text-base sm:text-lg text-[color:var(--cu-gold-light)]/80 mt-10">
            {t("a_different_perspective")}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-12">
            <button data-testid="entry-join-button" onClick={() => navigate("/apply")} className="btn-gold px-10 py-3.5 text-sm w-full sm:w-auto inline-flex items-center justify-center gap-2">
              {t("join")} <ArrowRight size={16} />
            </button>
            <button data-testid="entry-login-button" onClick={() => navigate("/login")} className="btn-ghost px-10 py-3.5 text-sm w-full sm:w-auto">
              {t("member_login")}
            </button>
          </div>

          <p className="text-xs text-[color:var(--cu-muted)] mt-8 max-w-md mx-auto leading-relaxed">{t("adults_only")}</p>
        </div>
      </div>

      {/* Membership card teaser */}
      <section className="px-6 py-16 max-w-md mx-auto text-center">
        <div className="eyebrow">{t("private_club")}</div>
        <img src={ASSETS.blueCard} alt="Caught Up International Membership Card" className="w-full rounded-2xl mt-6 gold-border" style={{ boxShadow: "0 30px 60px -25px rgba(0,0,60,0.8)" }} />
        <p className="font-serif-cu text-lg text-[color:var(--cu-text)] mt-8">Privacy · Discretion · Connection</p>
      </section>

      <CeoSection />

      {/* Corporate footer */}
      <footer className="px-6 pb-16 pt-6 max-w-2xl mx-auto text-center">
        <div className="gold-hairline mb-10" />
        <WhatsAppButton />
        <div className="mt-8 flex items-center justify-center gap-5 flex-wrap text-[color:var(--cu-muted)]">
          <button onClick={() => navigate("/corporate")} data-testid="entry-corporate-link" className="eyebrow hover:text-[color:var(--cu-gold-light)] transition-colors">{t("corporate")}</button>
          <button onClick={() => navigate("/corporate")} className="eyebrow hover:text-[color:var(--cu-gold-light)] transition-colors">{t("privacy_policy")}</button>
          <button onClick={() => navigate("/corporate")} className="eyebrow hover:text-[color:var(--cu-gold-light)] transition-colors">{t("membership_terms")}</button>
        </div>
        <p className="text-[0.65rem] text-[color:var(--cu-muted)] mt-8 tracking-[0.15em] font-mono-cu">© 2026 CAUGHTUP.INTERNATIONAL · {t("all_rights").toUpperCase()}</p>
      </footer>
    </div>
  );
}
